import React, { useState, useMemo } from 'react';
import { Box, Grid, Paper, Typography, CircularProgress, TextField, Button, FormControl, RadioGroup, FormControlLabel, Radio, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Pagination, Stack, Select, MenuItem, InputLabel, Tabs, Tab } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import useFetchObjects from '../../api/useFetchObjects';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import DocumentActivityReportSection from './DocumentActivityReportSection';
import LocalizedDate from '../../components/common/LocalizedDate';
import LocalizedDatePicker from '../../components/common/LocalizedDatePicker';
import { useDateFormat } from '../../context/DateFormatContext';

const SummaryCard = ({ title, value, color }) => (
  <Paper elevation={1} sx={{ p: 2, borderLeft: `4px solid ${color || '#1976d2'}` }}>
    <Typography variant="subtitle2" color="textSecondary" gutterBottom sx={{ fontSize: 12 }}>{title}</Typography>
    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{value ?? '--'}</Typography>
  </Paper>
);

const TaskReportSection = () => {
  const { user } = useAuth();
  const token = user?.token;
  const { t, i18n } = useTranslation(['reports', 'tasks', 'common']);
  const { dateFormat } = useDateFormat();
  const isRtlPage = ['dari', 'ps'].includes(i18n.language);
  const cellAlign = isRtlPage ? 'right' : 'left';

  // Filters
  const [fromDate, setFromDate] = useState(dayjs().subtract(30, 'day').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [mode, setMode] = useState('assignedTo'); // assignedTo (assignee) or assignedBy (assigner)
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [docType, setDocType] = useState('');

  // User filter (select a specific user)
  const [selectedUserId, setSelectedUserId] = useState('');

  // Fetch users for dropdown
  const { data: usersData } = useFetchObjects('users', { page: 1, limit: 500 }, token);

  // Normalize users list
  const users = useMemo(() => {
    if (!usersData) return [];
    if (Array.isArray(usersData)) return usersData;
    if (Array.isArray(usersData?.users)) return usersData.users;
    if (Array.isArray(usersData?.data)) return usersData.data;
    return [];
  }, [usersData]);

  // Ensure any change in the authenticated user updates the filter automatically
  const currentUserId = user?.id;

  // Choose endpoint and params based on the selected mode
  const endpointBase = mode === 'assignedTo' ? 'task-assignments/my-assignments' : 'task-assignments/assigned-by-me';

  // Build user-specific filter params based on mode
  const userFilterParams = selectedUserId
    ? (mode === 'assignedTo' ? { assigned_by_id: selectedUserId } : { assignee_id: selectedUserId })
    : {};

  const params = useMemo(() => ({
    page,
    limit,
    from_date: fromDate,
    to_date: toDate,
    ...(docType ? { document_type: docType } : {}),
    ...(mode === 'assignedTo' ? { assignee_id: currentUserId } : {}),
    ...userFilterParams
  }), [page, limit, fromDate, toDate, docType, mode, currentUserId, userFilterParams]);

  const { data: tasksData, isLoading: tasksLoading } = useFetchObjects(endpointBase, params, token);

  const tasks = useMemo(() => Array.isArray(tasksData) ? tasksData : (tasksData?.data || tasksData?.rows || tasksData?.tasks || []), [tasksData]);
  const totalPages = tasksData && !Array.isArray(tasksData) ? (tasksData.total_pages || tasksData.totalPages || 1) : 1;

  const handleFilter = () => {
    setPage(1);
    // React Query will refetch automatically because params key changed
  };

  // Compute status counts client-side to ensure cards always reflect table
  const statusCounts = useMemo(() => {
    const c = { ASSIGNED: 0, ACCEPTED: 0, IN_PROGRESS: 0, COMPLETED: 0, REJECTED: 0, FORWARDED: 0 };
    tasks.forEach((task) => {
      let st = task.status;
      // If forwarded chain completed, treat as its completed status
      if (st === 'FORWARDED' && task.metadata?.forwardedCompletion?.status) {
        st = task.metadata.forwardedCompletion.status;
      }
      if (c[st] !== undefined) c[st] += 1;
    });
    return c;
  }, [tasks]);

  const totalTasksFiltered = tasks.length;
  const completedCount = statusCounts.COMPLETED;

  const isAssignedBy = mode === 'assignedBy'; // Helper flag

  return (
    <Box sx={{ mt: 5 }} dir={isRtlPage ? 'rtl' : 'ltr'}>
      {/* <Typography variant="h5" gutterBottom align={isRtlPage ? 'right' : 'left'}>{t('reports:taskReport','Task Assignment Report')}</Typography> */}

      {/* Status summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }} dir={isRtlPage ? 'rtl' : 'ltr'}>
        <Grid item xs={6} sm={4} md={2}><SummaryCard title={t('reports:totalTasks','ټول')} value={totalTasksFiltered} color="#9C27B0" /></Grid>
        <Grid item xs={6} sm={4} md={2}><SummaryCard title={t('my_assignments:ASSIGNED','لیږل شوی')} value={statusCounts.ASSIGNED || 0} color="#1976d2" /></Grid>
        <Grid item xs={6} sm={4} md={2}><SummaryCard title={t('my_assignments:IN_PROGRESS','په جریان کی')} value={(statusCounts.IN_PROGRESS || 0) + (statusCounts.ACCEPTED || 0)} color="#03A9F4" /></Grid>
        <Grid item xs={6} sm={4} md={2}><SummaryCard title={t('my_assignments:COMPLETED','بشپړ شوی')} value={completedCount} color="#4caf50" /></Grid>
        <Grid item xs={6} sm={4} md={2}><SummaryCard title={t('my_assignments:REJECTED','رد شوی')} value={statusCounts.REJECTED || 0} color="#f44336" /></Grid>
        <Grid item xs={6} sm={4} md={2}><SummaryCard title={t('my_assignments:FORWARDED','راجع شوی')} value={statusCounts.FORWARDED || 0} color="#ff9800" /></Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center" dir={isRtlPage ? 'rtl' : 'ltr'}>
          <Grid item xs={12} md={3}>
            <LocalizedDatePicker
              label={t('common:from','From')}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              isRtl={isRtlPage}
              name="fromDate"
              placeholder={t('common:select_date', 'Select date')}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <LocalizedDatePicker
              label={t('common:to','To')}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              isRtl={isRtlPage}
              name="toDate"
              placeholder={t('common:select_date', 'Select date')}
            />
          </Grid>
          {/* User Filter */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="user-filter-label">{t('common:user', 'User')}</InputLabel>
              <Select
                labelId="user-filter-label"
                value={selectedUserId}
                label={t('common:user', 'User')}
                onChange={(e) => setSelectedUserId(e.target.value)}
              >
                <MenuItem value="">{t('common:all', 'All')}</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>{u.full_name || u.username}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Document Type Filter */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="doc-type-label">{t('documents:type', 'Doc Type')}</InputLabel>
              <Select
                labelId="doc-type-label"
                value={docType}
                label={t('documents:type', 'Doc Type')}
                onChange={(e) => setDocType(e.target.value)}
              >
                <MenuItem value="">{t('common:all', 'All')}</MenuItem>
                <MenuItem value="مکتوب">مکتوب</MenuItem>
                <MenuItem value="پیشنهاد">پیشنهاد</MenuItem>
                <MenuItem value="استعلام">استعلام</MenuItem>
                <MenuItem value="گزارش">گزارش</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Mode selector */}
          <Grid item xs={12} md={3}>
            <FormControl component="fieldset">
              <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
                <FormControlLabel value="assignedTo" control={<Radio />} label={t('tasks:assignedTo','Assigned To')} />
                <FormControlLabel value="assignedBy" control={<Radio />} label={t('tasks:assignedBy','Assigned By')} />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button variant="contained" fullWidth onClick={handleFilter}>{t('common:filter', 'Filter')}</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        {tasksLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}><CircularProgress /></Box>
        ) : (
          <TableContainer dir={isRtlPage ? 'rtl' : 'ltr'}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align={cellAlign}>{t('common:number','#')}</TableCell>
                  <TableCell align={cellAlign}>{t('documents:reference','Ref #')}</TableCell>
                  <TableCell align={cellAlign}>{t('documents:title','Title')}</TableCell>
                  <TableCell align={cellAlign}>{t('tasks:status','Status')}</TableCell>
                  <TableCell align={cellAlign}>{t('tasks:assignedAt','Assigned At')}</TableCell>
                  <TableCell align={cellAlign}>{t('tasks:dueDate','Due Date')}</TableCell>
                  <TableCell align={cellAlign}>{t('tasks:receivedAt','Received At')}</TableCell>
                  <TableCell align={cellAlign}>{t('tasks:completedAt','Completed At')}</TableCell>
                  <TableCell align={cellAlign}>{isAssignedBy ? t('tasks:assignee','Assignee') : t('tasks:assigner','Assigner')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.length === 0 ? (
                  <TableRow><TableCell colSpan={9} align="center">{t('common:no_records','No records')}</TableCell></TableRow>
                ) : tasks.map((task, idx) => (
                  <TableRow key={task.id} hover>
                    <TableCell align={cellAlign}>{(page - 1) * limit + idx + 1}</TableCell>
                    <TableCell align={cellAlign}>{task.document?.reference_number}</TableCell>
                    <TableCell align={cellAlign}>{task.document?.title}</TableCell>
                    <TableCell align={cellAlign}>{t(`tasks:status_${task.status?.toLowerCase()}`, task.status)}</TableCell>
                    <TableCell align={cellAlign}>
                      <LocalizedDate date={task.assigned_at} />
                    </TableCell>
                    <TableCell align={cellAlign}>
                      {task.due_date ? <LocalizedDate date={task.due_date} /> : '-'}
                    </TableCell>
                    <TableCell align={cellAlign}>
                      {task.accepted_at ? <LocalizedDate date={task.accepted_at} /> : '-'}
                    </TableCell>
                    <TableCell align={cellAlign}>
                      {task.completed_at ? <LocalizedDate date={task.completed_at} /> : '-'}
                    </TableCell>
                    <TableCell align={cellAlign}>
                      {isAssignedBy
                        ? (task.assignee?.full_name || task.assignee?.username)
                        : (task.assigner?.full_name || task.assigner?.username)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Pagination */}
      <Stack direction="row" justifyContent="center" sx={{ my: 2 }}>
        <Pagination count={Math.max(totalPages, 1)} page={page} onChange={(_, value) => setPage(value)} />
      </Stack>
    </Box>
  );
};

const Report = () => {
  const { user } = useAuth();
  const token = user?.token;
  const { t, i18n } = useTranslation(['reports', 'tasks', 'common']);
  const isRtlPage = ['dari', 'ps'].includes(i18n.language);
  const loading = false; // no top-level loading now, Task section handles its own

  const [selectedReport, setSelectedReport] = useState('TASK');

  return (
    <Box sx={{ p: 3 }} dir={isRtlPage ? 'rtl' : 'ltr'}>
      <Typography variant="h3" gutterBottom align={isRtlPage ? 'right' : 'left'}>
        {t('reports:systemReport', 'System Report')}
      </Typography>

      {/* Report Selector */}
      {/* <Tabs
        value={selectedReport}
        onChange={(_, v) => setSelectedReport(v)}
        sx={{ mb: 3 }}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
      >
        <Tab label={t('reports:taskReport', 'Task Assignment Report')} value="TASK" />
        <Tab label={t('reports:activityReport', 'Document Activity Report')} value="ACTIVITY" />
      </Tabs> */}

      {/* Conditional render */}
      {selectedReport === 'TASK' && <TaskReportSection />}
      {selectedReport === 'ACTIVITY' && <DocumentActivityReportSection />}
    </Box>
  );
};

export default Report; 